import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { brand } from '../../config/brand';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Loader2, Shield, User as UserIcon, Ban, UserCheck, Trash2, Edit2, Share2, Globe, X, RefreshCw, Coins, ArrowLeft, AlertTriangle, Mail, Receipt } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import moment from 'moment';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

interface SocialAccount {
  id: string;
  platform: string;
  name: string;
  handle?: string;
  avatarUrl?: string;
  language: string;
  createdAt: string;
}

interface User {
  id: string;
  email: string;
  name?: string;
  isAdmin: boolean;
  status: 'ACTIVE' | 'BANNED' | 'DELETED';
  storageUsed: number;
  totalSpent: number;
  lastManualStatsUpdate?: string;
  isEmailVerified: boolean;
  emailVerificationToken?: string;
  emailVerificationCode?: string;
  emailVerificationTokenExpiresAt?: string;
  tier: 'FREE' | 'BASIC' | 'PRO' | 'AGENCY';
  subscriptionStatus?: string;
  subscriptionEndsAt?: string;
  polarSubscriptionId?: string;
  isManualSubscription?: boolean;
  usedFreeMonthOffer: boolean;
  featureOverrides?: Record<string, boolean>;
  createdAt: string;
}

interface FeatureConfig {
  enabled: boolean;
  minTier: string;
}

export default function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const history = useHistory();
  const token = localStorage.getItem('token');
  const queryClient = useQueryClient();
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Edit state
  const [editingUser, setEditingUser] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    isAdmin: false,
    tier: 'FREE' as 'FREE' | 'BASIC' | 'PRO' | 'AGENCY',
    subscriptionStatus: '',
    subscriptionEndsAt: '',
    usedFreeMonthOffer: false
  });

  // Social accounts state
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
  const [loadingSocial, setLoadingSocial] = useState(false);

  // Consumption state
  const [viewingConsumption, setViewingConsumption] = useState(false);
  const [consumptionSummary, setConsumptionSummary] = useState<any[]>([]);
  const [loadingConsumption, setLoadingConsumption] = useState(false);

  // Purge confirmation
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [purging, setPurging] = useState(false);

  // Permanent Delete confirmation
  const [showPermanentDeleteDialog, setShowPermanentDeleteDialog] = useState(false);
  const [deletingPermanently, setDeletingPermanently] = useState(false);

  // Delete Orders confirmation
  const [showDeleteOrdersDialog, setShowDeleteOrdersDialog] = useState(false);
  const [deletingOrders, setDeletingOrders] = useState(false);

  // Manual subscription state
  const [showManualSubDialog, setShowManualSubDialog] = useState(false);
  const [manualSubTier, setManualSubTier] = useState<string>('BASIC');
  const [manualSubEndsAt, setManualSubEndsAt] = useState<string>(moment().add(30, 'days').format('YYYY-MM-DD'));
  // Orders state
  const [orders, setOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Feature overrides state
  const [featureOverrides, setFeatureOverrides] = useState<Record<string, boolean>>({});
  const [globalFeatures, setGlobalFeatures] = useState<Record<string, FeatureConfig>>({});

  const { data: user, isLoading, error } = useQuery<User>({
    queryKey: ['admin-user', userId],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token && !!userId
  });

  useEffect(() => {
    if (user) {
      setEditFormData({
        name: user.name || '',
        email: user.email,
        isAdmin: user.isAdmin,
        tier: user.tier,
        subscriptionStatus: user.subscriptionStatus || '',
        subscriptionEndsAt: user.subscriptionEndsAt || '',
        usedFreeMonthOffer: user.usedFreeMonthOffer
      });
      setFeatureOverrides(user.featureOverrides || {});
      fetchSocialAccounts();
      fetchGlobalFeatures();
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    if (!userId) return;
    setLoadingOrders(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/users/${userId}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoadingOrders(false);
    }
  };

  // Helper to determine plan type from last order
  const getPlanType = () => {
    // Don't show plan type if user is FREE tier
    if (!user || user.tier === 'FREE' || orders.length === 0) return null;
    
    const lastOrder = orders[0];
    const variantId = lastOrder.variantId;
    
    // Only show plan type if the order tier matches current user tier
    const orderTierMatches = 
      (user.tier === 'BASIC' && lastOrder.tier === 'BASIC') ||
      (user.tier === 'PRO' && lastOrder.tier === 'PRO') ||
      (user.tier === 'AGENCY' && lastOrder.tier === 'AGENCY');
    
    if (!orderTierMatches) return null;
    
    // Polar Product IDs (from env)
    const polarProducts = {
      BasicMonthly: 'aea684fb-ad3f-48f6-9197-96a45ae8f36b',
      BasicYearly: '53c87aed-d24a-47c2-bc84-01f4f4d426cc',
      IndieMonthly: '1f7f64ed-e1b9-4221-9445-a53981753b9e',
      IndieYearly: '58defef8-a583-4a32-b1c6-9f378f94a50e'
    };
    
    // Check Polar IDs
    if (variantId === polarProducts.BasicMonthly) return 'Basic Monthly';
    if (variantId === polarProducts.BasicYearly) return 'Basic Yearly';
    if (variantId === polarProducts.IndieMonthly) return `${brand.planNames.pro} Monthly`;
    if (variantId === polarProducts.IndieYearly) return `${brand.planNames.pro} Yearly`;
    
    // Fallback to amount-based detection
    const amount = lastOrder.amount / 100;
    if (user.tier === 'BASIC') {
      return amount > 10 ? 'Basic Yearly' : 'Basic Monthly';
    } else if (user.tier === 'PRO') {
      return amount > 50 ? `${brand.planNames.pro} Yearly` : `${brand.planNames.pro} Monthly`;
    }
    
    return null;
  };

  const fetchGlobalFeatures = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlobalFeatures(response.data.features || {});
    } catch (error) {
      console.error('Failed to fetch global features');
    }
  };

  const updateFeaturesMutation = useMutation({
    mutationFn: async (overrides: Record<string, boolean>) => {
      const response = await axios.put(`${API_BASE_URL}/admin/users/${userId}/features`, {
        featureOverrides: overrides
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      toast.success('Feature overrides updated');
    },
    onError: () => toast.error('Failed to update feature overrides')
  });

  const handleOverrideChange = (featureKey: string, enabled: boolean) => {
    const newOverrides = { ...featureOverrides, [featureKey]: enabled };
    setFeatureOverrides(newOverrides);
    updateFeaturesMutation.mutate(newOverrides);
  };

  const handleRemoveOverride = (featureKey: string) => {
    const newOverrides = { ...featureOverrides };
    delete newOverrides[featureKey];
    setFeatureOverrides(newOverrides);
    updateFeaturesMutation.mutate(newOverrides);
  };

  const isFeatureEnabledByTier = (featureKey: string) => {
    if (!user) return false;
    const feature = globalFeatures[featureKey];
    if (!feature) return false;

    const tiers = ['FREE', 'BASIC', 'PRO', 'AGENCY'];
    const userTierIndex = tiers.indexOf(user.tier);
    const minTierIndex = tiers.indexOf(feature.minTier);

    return userTierIndex >= minTierIndex;
  };

  const fetchSocialAccounts = async () => {
    if (!userId) return;
    setLoadingSocial(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/users/${userId}/social-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSocialAccounts(response.data);
    } catch (error) {
      toast.error('Failed to fetch social accounts');
    } finally {
      setLoadingSocial(false);
    }
  };

  const fetchConsumptionSummary = async () => {
    if (!userId) return;
    setLoadingConsumption(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/users/${userId}/consumption`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsumptionSummary(response.data);
    } catch (error) {
      toast.error('Failed to fetch consumption summary');
    } finally {
      setLoadingConsumption(false);
    }
  };

  const updateSocialLanguageMutation = useMutation({
    mutationFn: async ({ id, language }: { id: string, language: string }) => {
      const response = await axios.put(`${API_BASE_URL}/admin/social-accounts/${id}`, { language }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: (data) => {
      setSocialAccounts(prev => prev.map(acc => acc.id === data.id ? data : acc));
      toast.success('Language updated');
    },
    onError: () => toast.error('Failed to update language')
  });

  const deleteSocialMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_BASE_URL}/admin/social-accounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return id;
    },
    onSuccess: (id) => {
      setSocialAccounts(prev => prev.filter(acc => acc.id !== id));
      toast.success('Social account removed');
    },
    onError: () => toast.error('Failed to remove social account')
  });

  const detectLanguageMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.post(`${API_BASE_URL}/admin/social-accounts/${id}/detect-language`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      toast.success('Language detection job triggered');
    },
    onError: () => toast.error('Failed to trigger language detection')
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string, status: string }) => {
      setUpdatingId(id);
      const response = await axios.put(`${API_BASE_URL}/admin/users/${id}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('User status updated');
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
    onSettled: () => {
      setUpdatingId(null);
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: async (data: { id: string, name: string, email: string, isAdmin: boolean, tier: string, subscriptionStatus?: string, subscriptionEndsAt?: string, usedFreeMonthOffer?: boolean }) => {
      setUpdatingId(data.id);
      const response = await axios.put(`${API_BASE_URL}/admin/users/${data.id}`, {
        name: data.name,
        email: data.email,
        isAdmin: data.isAdmin,
        tier: data.tier,
        subscriptionStatus: data.subscriptionStatus,
        subscriptionEndsAt: data.subscriptionEndsAt,
        usedFreeMonthOffer: data.usedFreeMonthOffer
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('User updated successfully');
      setEditingUser(false);
      setShowManualSubDialog(false); // Close manual sub dialog on success
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
    onSettled: () => {
      setUpdatingId(null);
    }
  });

  const purgeUserDataMutation = useMutation({
    mutationFn: async () => {
      setPurging(true);
      const response = await axios.post(`${API_BASE_URL}/admin/users/${userId}/purge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('User posts and videos purged successfully');
      setShowPurgeDialog(false);
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
    onSettled: () => {
      setPurging(false);
    }
  });

  const permanentDeleteUserMutation = useMutation({
    mutationFn: async () => {
      setDeletingPermanently(true);
      const response = await axios.delete(`${API_BASE_URL}/admin/users/${userId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('User deleted permanently from the database');
      history.push('/admin');
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
    onSettled: () => {
      setDeletingPermanently(false);
    }
  });

  const deleteOrdersMutation = useMutation({
    mutationFn: async () => {
      setDeletingOrders(true);
      const response = await axios.delete(`${API_BASE_URL}/admin/users/${userId}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      toast.success('User orders cleared successfully');
      setShowDeleteOrdersDialog(false);
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    },
    onSettled: () => {
      setDeletingOrders(false);
    }
  });

  const resendVerificationMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API_BASE_URL}/admin/users/${userId}/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      toast.success('Verification email sent successfully');
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    }
  });

  const handleSaveEdit = () => {
    if (!user) return;
    updateUserMutation.mutate({
      id: user.id,
      ...editFormData,
      usedFreeMonthOffer: user.usedFreeMonthOffer // Ensure this is passed if not in form
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>;
      case 'BANNED':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Banned</Badge>;
      case 'DELETED':
        return <Badge className="bg-slate-500/20 text-muted-foreground border-slate-500/30">Deleted</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="text-red-500 p-4 bg-red-500/10 rounded-xl border border-red-500/20">
        Error loading user: ${(error as any)?.message || 'User not found'}
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => history.push('/admin')}
          className="mb-4 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </Button>
        <h1 className="text-3xl font-black text-white flex items-center gap-3">
          <UserIcon className="h-8 w-8 text-primary" />
          {user.name || 'Anonymous User'}
        </h1>
        <p className="text-muted-foreground mt-2 font-mono text-sm">{user.email}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* User Information Card */}
          <Card className="bg-card border-border text-foreground shadow-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-black flex items-center gap-2">
                  User Information
                </CardTitle>
                {!editingUser && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingUser(true)}
                    className="text-primary hover:text-primary/80"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingUser ? (
                <div className="space-y-4">
                  <div className="grid gap-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="name" className="text-right text-xs font-bold uppercase text-muted-foreground">
                        Name
                      </Label>
                      <Input
                        id="name"
                        value={editFormData.name}
                        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                        className="col-span-3 bg-muted border-border h-9"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="email" className="text-right text-xs font-bold uppercase text-muted-foreground">
                        Email
                      </Label>
                      <Input
                        id="email"
                        value={editFormData.email}
                        onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        className="col-span-3 bg-muted border-border h-9"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="isAdmin" className="text-right text-xs font-bold uppercase text-muted-foreground">
                        Admin Access
                      </Label>
                      <div className="col-span-3 flex items-center">
                        <Switch
                          id="isAdmin"
                          checked={editFormData.isAdmin}
                          onCheckedChange={(checked) => setEditFormData({ ...editFormData, isAdmin: checked })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="tier" className="text-right text-xs font-bold uppercase text-muted-foreground">
                        User Tier
                      </Label>
                      <Select
                        value={editFormData.tier}
                        onValueChange={(val: any) => setEditFormData({ ...editFormData, tier: val })}
                      >
                        <SelectTrigger className="col-span-3 bg-muted border-border h-9">
                          <SelectValue placeholder="Select Tier" />
                        </SelectTrigger>
                        <SelectContent className="bg-muted border-border text-foreground">
                          <SelectItem value="FREE">Free (Cancel active sub in LS)</SelectItem>
                          <SelectItem value="BASIC">Basic</SelectItem>
                          <SelectItem value="PRO">Indie</SelectItem>
                          <SelectItem value="AGENCY">Agency</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="subStatus" className="text-right text-xs font-bold uppercase text-muted-foreground">
                        Status
                      </Label>
                      <Input
                        id="subStatus"
                        placeholder="active, cancelled, expired..."
                        value={editFormData.subscriptionStatus}
                        onChange={(e) => setEditFormData({ ...editFormData, subscriptionStatus: e.target.value })}
                        className="col-span-3 bg-muted border-border h-9"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="endsAt" className="text-right text-xs font-bold uppercase text-muted-foreground">
                        Ends At
                      </Label>
                      <Input
                        id="endsAt"
                        type="datetime-local"
                        value={editFormData.subscriptionEndsAt ? moment(editFormData.subscriptionEndsAt).format('YYYY-MM-DDTHH:mm') : ''}
                        onChange={(e) => setEditFormData({ ...editFormData, subscriptionEndsAt: e.target.value })}
                        className="col-span-3 bg-muted border-border h-9"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingUser(false);
                        setEditFormData({
                          name: user.name || '',
                          email: user.email,
                          isAdmin: user.isAdmin,
                          tier: user.tier,
                          subscriptionStatus: user.subscriptionStatus || '',
                          subscriptionEndsAt: user.subscriptionEndsAt || '',
                          usedFreeMonthOffer: user.usedFreeMonthOffer
                        });
                      }}
                      className="border-border hover:bg-card"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveEdit}
                      disabled={updateUserMutation.isPending}
                      className="bg-primary hover:bg-primary/90"
                    >
                      {updateUserMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs font-bold uppercase text-muted-foreground">Role</Label>
                    <div className="mt-2">
                      {user.isAdmin ? (
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 gap-1">
                          <Shield className="h-3 w-3" />
                          Admin
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-border text-muted-foreground">
                          User
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-muted-foreground">Status</Label>
                    <div className="mt-2">
                      {getStatusBadge(user.status)}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-muted-foreground">Tier</Label>
                    <div className="mt-2 space-y-1">
                      <Badge className={
                        user.tier === 'FREE' ? "bg-slate-500/20 text-muted-foreground border-slate-500/30" :
                          user.tier === 'BASIC' ? "bg-primary/20 text-primary border-primary/30" :
                            user.tier === 'PRO' ? "bg-purple-500/20 text-purple-400 border-purple-500/30" :
                              "bg-amber-500/20 text-amber-400 border-amber-500/30"
                      }>
                        {user.tier === 'PRO' ? 'INDIE' : user.tier}
                      </Badge>
                      {getPlanType() && (
                        <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-wide">
                          {getPlanType()}
                        </div>
                      )}
                      {user.subscriptionEndsAt && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">Ends:</span>
                          <span className={
                            moment(user.subscriptionEndsAt).isBefore(moment()) ? "text-[10px] font-black font-mono text-red-500" : "text-[10px] font-black font-mono text-primary"
                          }>
                            {moment(user.subscriptionEndsAt).format('MMM D, YYYY')}
                          </span>
                        </div>
                      )}
                      {user.subscriptionStatus && (
                        <div className="text-[9px] text-muted-foreground font-bold uppercase tracking-tight">
                          Status: {user.subscriptionStatus}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-muted-foreground">Active Subscription</Label>
                    <div className="mt-2">
                      <Badge className={
                        (user as any).polarSubscriptionId
                          ? "bg-green-500/20 text-green-400 border-green-500/30"
                          : "bg-slate-500/20 text-muted-foreground border-slate-500/30"
                      }>
                        {(user as any).polarSubscriptionId ? 'YES' : 'NO'}
                      </Badge>
                      {(user as any).polarSubscriptionId && (
                        <div className="mt-1 text-[10px] text-muted-foreground font-mono">
                          Polar ID
                        </div>
                      )}
                      {!(user as any).polarSubscriptionId && (
                        <div className="mt-1 text-[10px] text-muted-foreground italic">
                          Manual plan
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-muted-foreground">Storage Used</Label>
                    <div className="mt-2 text-foreground font-mono text-sm">
                      {(user.storageUsed / (1024 * 1024 * 1024)).toFixed(2)} GB
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-muted-foreground">Total Spent</Label>
                    <div className="mt-2 text-green-400 font-black font-mono text-sm">
                      ${user.totalSpent.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-muted-foreground">Last Sync</Label>
                    <div className="mt-2 text-muted-foreground text-sm">
                      {user.lastManualStatsUpdate ? (
                        <div className="flex flex-col">
                          <span className="font-bold text-foreground">{moment(user.lastManualStatsUpdate).format('MMM D, HH:mm')}</span>
                          <span className="text-xs text-muted-foreground">{moment(user.lastManualStatsUpdate).fromNow()}</span>
                        </div>
                      ) : (
                        <span className="italic text-muted-foreground">Never</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-muted-foreground">Joined</Label>
                    <div className="mt-2 text-muted-foreground text-sm">
                      {moment(user.createdAt).format('LL')}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email Verification Details (Admin Only) */}
          {!user.isEmailVerified && (
            <Card className="bg-amber-500/5 border-amber-500/20 text-foreground shadow-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-black flex items-center gap-2 text-amber-400">
                  <Mail className="h-5 w-5" />
                  Verification Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground">Current Code</Label>
                    <div className="font-mono text-2xl font-black text-amber-500 tracking-widest">
                      {user.emailVerificationCode || 'N/A'}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground">Token Expiry</Label>
                    <div className="text-sm text-foreground font-medium">
                      {user.emailVerificationTokenExpiresAt ? (
                        <>
                          {moment(user.emailVerificationTokenExpiresAt).format('MMM D, HH:mm')}
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({moment(user.emailVerificationTokenExpiresAt).fromNow()})
                          </span>
                        </>
                      ) : 'N/A'}
                    </div>
                  </div>
                  <div className="md:col-span-2 space-y-1">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground">Verification Link</Label>
                    <div className="bg-background p-2 rounded border border-border text-[10px] font-mono break-all text-muted-foreground">
                      {window.location.origin}/verify-email?token={user.emailVerificationToken}
                    </div>
                  </div>
                </div>
                <div className="mt-6">
                  <Button
                    variant="outline"
                    className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10 font-bold"
                    onClick={() => resendVerificationMutation.mutate()}
                    disabled={resendVerificationMutation.isPending}
                  >
                    {resendVerificationMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                    Resend Verification Email
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Social Connections Card */}
          <Card className="bg-card border-border text-foreground shadow-2xl">
            <CardHeader>
              <CardTitle className="text-xl font-black flex items-center gap-2">
                <Share2 className="h-5 w-5 text-primary" />
                Social Connections
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingSocial ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : socialAccounts.length === 0 ? (
                <div className="text-center py-8 bg-muted/50 rounded-xl border border-dashed border-border text-muted-foreground text-xs">
                  No social accounts connected to this user.
                </div>
              ) : (
                <div className="space-y-2">
                  {socialAccounts.map((account) => (
                    <div key={account.id} className="flex items-center justify-between p-3 bg-muted rounded-xl border border-border group hover:border-border transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-card flex items-center justify-center overflow-hidden">
                          {account.avatarUrl ? (
                            <img src={account.avatarUrl} alt={account.name} className="h-full w-full object-cover" />
                          ) : (
                            <Globe className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <div className="text-xs font-bold text-foreground">{account.name}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">
                            {account.platform} • {account.handle || 'No handle'}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 mr-2">
                          <Label className="text-[9px] uppercase font-black text-muted-foreground">Lang</Label>
                          <Select
                            value={account.language}
                            onValueChange={(lang) => updateSocialLanguageMutation.mutate({ id: account.id, language: lang })}
                          >
                            <SelectTrigger className="h-7 w-16 bg-background border-border text-[10px] font-bold">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-muted border-border text-foreground">
                              <SelectItem value="en">EN</SelectItem>
                              <SelectItem value="es">ES</SelectItem>
                              <SelectItem value="fr">FR</SelectItem>
                              <SelectItem value="pt">PT</SelectItem>
                              <SelectItem value="de">DE</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                          onClick={() => detectLanguageMutation.mutate(account.id)}
                          disabled={detectLanguageMutation.isPending}
                          title="Auto-detect language"
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${detectLanguageMutation.isPending ? 'animate-spin' : ''}`} />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to remove this social connection?')) {
                              deleteSocialMutation.mutate(account.id);
                            }
                          }}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feature Management Card */}
          <Card className="bg-card border-border text-foreground shadow-2xl">
            <CardHeader>
              <CardTitle className="text-xl font-black flex items-center gap-2">
                <Shield className="h-5 w-5 text-purple-400" />
                Feature Management
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Control access to specific features through manual overrides.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.keys(globalFeatures).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-xs italic">
                    Loading system configurations...
                  </div>
                ) : (
                  Object.entries(globalFeatures).map(([key, config]) => {
                    const hasOverride = featureOverrides[key] !== undefined;
                    const isEnabled = hasOverride ? featureOverrides[key] : isFeatureEnabledByTier(key);
                    const tierEnabled = isFeatureEnabledByTier(key);

                    return (
                      <div key={key} className="flex items-center justify-between p-4 bg-muted rounded-2xl border border-border group hover:border-border transition-all">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-foreground capitalize">
                              {key.replace(/_/g, ' ')}
                            </span>
                            {hasOverride && (
                              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-[8px] h-4 uppercase font-black">
                                Override
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-[10px]">
                            <span className="text-muted-foreground uppercase font-black">Tier: {config.minTier}+</span>
                            <span className="text-muted-foreground">•</span>
                            <span className={tierEnabled ? "text-green-500/70" : "text-red-500/70"}>
                              {tierEnabled ? "Inherited: ON" : "Inherited: OFF"}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          {hasOverride && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveOverride(key)}
                              className="h-7 px-2 text-[9px] font-black uppercase text-muted-foreground hover:text-red-400"
                            >
                              Reset
                            </Button>
                          )}
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={(val) => handleOverrideChange(key, val)}
                            className="data-[state=checked]:bg-purple-600"
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment History Card */}
          <Card className="bg-card border-border text-foreground shadow-2xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-xl font-black flex items-center gap-2">
                <Receipt className="h-5 w-5 text-green-400" />
                Payment History
              </CardTitle>
              {orders.length > 0 && (
                <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                  {orders.length} transactions
                </div>
              )}
            </CardHeader>
            <CardContent>
              {loadingOrders ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 bg-muted/50 rounded-xl border border-dashed border-border text-muted-foreground text-xs italic">
                  No payment records found for this user.
                </div>
              ) : (
                <div className="rounded-xl border border-border overflow-hidden">
                  <Table>
                    <TableHeader className="bg-muted/50">
                      <TableRow className="border-border">
                        <TableHead className="text-[10px] uppercase font-black text-muted-foreground">Tier</TableHead>
                        <TableHead className="text-[10px] uppercase font-black text-muted-foreground">Amount</TableHead>
                        <TableHead className="text-[10px] uppercase font-black text-muted-foreground">Date</TableHead>
                        <TableHead className="text-[10px] uppercase font-black text-muted-foreground">Order ID</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.id} className="border-border hover:bg-card/30">
                          <TableCell>
                            <Badge className={
                              order.tier === 'PRO' ? "bg-purple-500/20 text-purple-400" :
                                order.tier === 'BASIC' ? "bg-primary/20 text-primary" :
                                  "bg-slate-500/20 text-muted-foreground"
                            }>
                              {order.tier === 'PRO' ? 'INDIE' : order.tier}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs font-bold text-foreground">
                            ${(order.amount / 100).toFixed(2)} {order.currency}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {moment(order.createdAt).format('LL')}
                          </TableCell>
                          <TableCell className="text-[10px] font-mono text-muted-foreground">
                            #{order.polarOrderId || order.id}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar Actions */}
        <div className="space-y-6">
          {/* Actions Card */}
          <Card className="bg-card border-border text-foreground shadow-2xl">
            <CardHeader>
              <CardTitle className="text-xl font-black">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5 mb-4">
                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Change Tier</Label>
                <div className="flex gap-2">
                  <Select
                    value={user.tier}
                    onValueChange={(newTier) => updateUserMutation.mutate({
                      id: user.id,
                      name: user.name || '',
                      email: user.email,
                      isAdmin: user.isAdmin,
                      tier: newTier as 'FREE' | 'BASIC' | 'PRO' | 'AGENCY',
                      usedFreeMonthOffer: user.usedFreeMonthOffer
                    })}
                    disabled={updateUserMutation.isPending}
                  >
                    <SelectTrigger className="bg-muted border-border h-9 text-xs font-bold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-muted border-border text-foreground">
                      <SelectItem value="FREE">FREE (Cancel LS sub)</SelectItem>
                      <SelectItem value="BASIC">BASIC</SelectItem>
                      <SelectItem value="PRO">INDIE</SelectItem>
                      <SelectItem value="AGENCY">AGENCY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5 mb-4">
                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Advanced</Label>
                <Button
                  className="w-full bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 font-black h-9 text-[10px] uppercase tracking-widest"
                  onClick={() => {
                    setManualSubTier(user.tier === 'FREE' ? 'BASIC' : user.tier);
                    setShowManualSubDialog(true);
                  }}
                  disabled={updateUserMutation.isPending}
                >
                  Create Manual Subscription
                </Button>
              </div>
              <div className="space-y-1.5 mb-4">
                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Offer Management</Label>
                <div className="bg-muted/50 p-3 rounded-xl border border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase">Gift Used:</span>
                    <Badge className={user.usedFreeMonthOffer ? "bg-red-500/20 text-red-400" : "bg-green-500/20 text-green-400"}>
                      {user.usedFreeMonthOffer ? 'YES' : 'NO'}
                    </Badge>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full h-8 text-[10px] font-black uppercase tracking-widest border-border"
                    onClick={async () => {
                      if (window.confirm('Reset this user\'s free month offer status?')) {
                        try {
                          await axios.post(`${API_BASE_URL}/admin/users/${userId}/reset-free-offer`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
                          toast.success('Offer status reset');
                        } catch (e: any) {
                          toast.error(e.response?.data?.error || 'Failed to reset offer');
                        }
                      }
                    }}
                  >
                    Reset Gift Offer
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5 mb-4">
                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Testing Tools</Label>
                <div className="bg-muted/50 p-3 rounded-xl border border-border space-y-2">
                  <Button
                    variant="outline"
                    className="w-full h-8 text-[10px] font-black uppercase tracking-widest border-primary/30 text-primary hover:bg-primary/10"
                    onClick={async () => {
                      try {
                        await axios.post(`${API_BASE_URL}/admin/users/${userId}/mock-subscription-date`, { daysAgo: 35 }, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
                        toast.success('Subscription date mocked to > 1 month ago');
                      } catch (e: any) {
                        toast.error(e.response?.data?.error || 'Failed to mock date');
                      }
                    }}
                  >
                    Mock 1+ Month Sub
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full h-8 text-[10px] font-black uppercase tracking-widest border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                    onClick={() => {
                      const expireAt = new Date(Date.now() + 30000).toISOString();
                      updateUserMutation.mutate({
                        id: user.id,
                        name: user.name || '',
                        email: user.email,
                        isAdmin: user.isAdmin,
                        tier: user.tier,
                        subscriptionEndsAt: expireAt,
                        subscriptionStatus: 'active'
                      });
                    }}
                  >
                    Expire in 30s
                  </Button>
                  <p className="text-[9px] text-muted-foreground text-center italic">Useful for testing access logic and expiration UI.</p>
                </div>
              </div>
              <Separator className="bg-card my-4" />
              <Button
                variant="outline"
                className="w-full justify-start text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/10"
                onClick={() => {
                  setViewingConsumption(true);
                  fetchConsumptionSummary();
                }}
              >
                <Coins className="h-4 w-4 mr-2" />
                AI/API Usage
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-green-400 border-green-500/30 hover:bg-green-500/10"
                onClick={() => updateStatusMutation.mutate({ id: user.id, status: 'ACTIVE' })}
                disabled={updatingId === user.id || user.status === 'ACTIVE'}
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Mark Active
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-orange-400 border-orange-500/30 hover:bg-orange-500/10"
                onClick={() => updateStatusMutation.mutate({ id: user.id, status: 'BANNED' })}
                disabled={updatingId === user.id || user.status === 'BANNED'}
              >
                <Ban className="h-4 w-4 mr-2" />
                Ban User
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-red-400 border-red-500/30 hover:bg-red-500/10"
                onClick={() => {
                  if (window.confirm('Are you sure you want to mark this user as DELETED? This will prevent login but keep records.')) {
                    updateStatusMutation.mutate({ id: user.id, status: 'DELETED' });
                  }
                }}
                disabled={updatingId === user.id || user.status === 'DELETED'}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Mark as Deleted
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-red-600 border-red-600/50 hover:bg-red-600/20 font-bold"
                onClick={() => setShowPermanentDeleteDialog(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Permanently
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-red-500 border-red-500/30 hover:bg-red-500/10"
                onClick={() => setShowDeleteOrdersDialog(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Order History
              </Button>
              <Separator className="bg-card my-4" />
              <Button
                variant="outline"
                className="w-full justify-start text-red-500 border-red-500/50 hover:bg-red-500/20"
                onClick={() => setShowPurgeDialog(true)}
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Purge All Posts & Videos
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Consumption Dialog */}
        <Dialog open={viewingConsumption} onOpenChange={setViewingConsumption}>
          <DialogContent className="bg-card border-border text-foreground sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-black flex items-center gap-2">
                <Coins className="h-5 w-5 text-yellow-500" />
                Usage Breakdown
              </DialogTitle>
              <DialogDescription className="text-muted-foreground font-medium">
                Detailed consumption for <span className="text-foreground font-bold">{user.name || user.email}</span>
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              {loadingConsumption ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : consumptionSummary.length === 0 ? (
                <div className="text-center py-12 bg-muted/50 rounded-2xl border border-dashed border-border">
                  <Coins className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm font-bold">No usage data found for this user.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-xl border border-border bg-muted/30 overflow-hidden">
                    <Table>
                      <TableHeader className="bg-muted/50">
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[9px]">Feature</TableHead>
                          <TableHead className="text-right text-muted-foreground font-bold uppercase tracking-widest text-[9px]">Usage</TableHead>
                          <TableHead className="text-right text-muted-foreground font-bold uppercase tracking-widest text-[9px]">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {consumptionSummary.map((record, idx) => (
                          <TableRow key={idx} className="border-border hover:bg-card/30 transition-colors">
                            <TableCell>
                              <div className="space-y-0.5">
                                <div className="text-foreground text-xs font-bold">{record.feature}</div>
                                <div className="flex items-center gap-1.5">
                                  <Badge variant="outline" className="px-1 py-0 h-3.5 text-[8px] border-border text-muted-foreground uppercase">
                                    {record.type}
                                  </Badge>
                                  {record.model && (
                                    <span className="text-[9px] text-purple-400/60 font-mono italic">{record.model}</span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-foreground font-bold font-mono text-xs">
                                {record._sum.amount?.toLocaleString()}
                              </span>
                              <span className="ml-1 text-[8px] text-muted-foreground font-black uppercase">
                                {record.type === 'OPENAI' ? 'tk' : 'cr'}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-green-400 font-black font-mono text-xs">
                                ${record.totalCost.toFixed(4)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-primary/5 rounded-xl border border-primary/10">
                    <span className="text-xs font-black uppercase tracking-widest text-muted-foreground">Total Estimated Spent</span>
                    <span className="text-lg font-black text-primary font-mono">
                      ${consumptionSummary.reduce((acc, curr) => acc + curr.totalCost, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setViewingConsumption(false)} className="border-border hover:bg-card">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Purge Confirmation Dialog */}
        <Dialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
          <DialogContent className="bg-card border-border text-foreground sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-black flex items-center gap-2 text-red-400">
                <AlertTriangle className="h-5 w-5" />
                Purge All User Data
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                This action will <strong className="text-red-400">permanently delete</strong> all posts and videos for <strong className="text-foreground">{user.name || user.email}</strong>. This cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-3">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="text-sm text-foreground">
                  <strong className="text-red-400">Warning:</strong> This will:
                </p>
                <ul className="list-disc list-inside mt-2 text-xs text-muted-foreground space-y-1">
                  <li>Delete all posts created by this user</li>
                  <li>Delete all associated media files (videos, images)</li>
                  <li>Remove all post schedules and analytics data</li>
                  <li>Free up storage space</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowPurgeDialog(false)}
                className="border-border hover:bg-card"
                disabled={purging}
              >
                Cancel
              </Button>
              <Button
                onClick={() => purgeUserDataMutation.mutate()}
                disabled={purging}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {purging ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Purging...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Purge All Data
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Permanent Delete Confirmation Dialog */}
        <Dialog open={showPermanentDeleteDialog} onOpenChange={setShowPermanentDeleteDialog}>
          <DialogContent className="bg-card border-border text-foreground sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-black flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                PERMANENT DELETION
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                This action will <strong className="text-red-600">completely and permanently erase</strong> <strong className="text-foreground">{user.name || user.email}</strong> from the database.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-3">
              <div className="bg-red-600/10 border border-red-600/30 rounded-xl p-4">
                <p className="text-sm text-foreground">
                  <strong className="text-red-600">CRITICAL WARNING:</strong> This is irreversible.
                </p>
                <ul className="list-disc list-inside mt-2 text-xs text-muted-foreground space-y-1">
                  <li>The user record will be deleted from the database</li>
                  <li>The email <strong className="text-foreground">{user.email}</strong> will be available for new registration</li>
                  <li>All posts, media, social connections and history will be GONE</li>
                  <li>AI logs and consumption records will be anonymized (user reference removed)</li>
                </ul>
              </div>
              <p className="text-[10px] text-muted-foreground italic">
                Use this when a user wants their data completely removed or for testing purposes.
              </p>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowPermanentDeleteDialog(false)}
                className="border-border hover:bg-card"
                disabled={deletingPermanently}
              >
                Cancel
              </Button>
              <Button
                onClick={() => permanentDeleteUserMutation.mutate()}
                disabled={deletingPermanently}
                className="bg-red-600 hover:bg-red-700 text-white font-bold"
              >
                {deletingPermanently ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    YES, DELETE PERMANENTLY
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Orders Confirmation Dialog */}
        <Dialog open={showDeleteOrdersDialog} onOpenChange={setShowDeleteOrdersDialog}>
          <DialogContent className="bg-card border-border text-foreground sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-black flex items-center gap-2 text-red-400">
                <Trash2 className="h-5 w-5" />
                Clear Order History
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                This will <span className="text-red-400 font-bold uppercase tracking-tighter">delete all saved transactions</span> for <span className="text-foreground font-bold">{user.name || user.email}</span>.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  This action only deletes the <strong className="text-foreground uppercase">local database records</strong> of the orders. It will NOT affect the actual subscriptions or payments in Lemon Squeezy.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteOrdersDialog(false)}
                className="border-border hover:bg-card"
                disabled={deletingOrders}
              >
                Cancel
              </Button>
              <Button
                onClick={() => deleteOrdersMutation.mutate()}
                disabled={deletingOrders}
                className="bg-red-600 hover:bg-red-700 text-white font-bold"
              >
                {deletingOrders ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    DELETE ORDERS
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Manual Subscription Modal */}
      <Dialog open={showManualSubDialog} onOpenChange={setShowManualSubDialog}>
        <DialogContent className="max-w-md bg-card border-border rounded-[2.5rem] text-foreground">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black italic uppercase">Create Subscription</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Manually grant a paid tier to this user with a fixed expiration date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase text-muted-foreground">Select Tier</Label>
              <Select
                value={manualSubTier}
                onValueChange={setManualSubTier}
              >
                <SelectTrigger className="bg-muted border-border h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border text-foreground">
                  <SelectItem value="BASIC">BASIC</SelectItem>
                  <SelectItem value="PRO">INDIE</SelectItem>
                  <SelectItem value="AGENCY">AGENCY</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase text-muted-foreground">Expiration Date</Label>
              <Input
                type="date"
                value={manualSubEndsAt}
                onChange={(e) => setManualSubEndsAt(e.target.value)}
                className="bg-muted border-border h-10"
              />
              <p className="text-[10px] text-muted-foreground italic">
                The user will be automatically downgraded to FREE on this date.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowManualSubDialog(false)}
              className="border-border"
            >
              Cancel
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-white font-black"
              onClick={() => {
                updateUserMutation.mutate({
                  id: user!.id,
                  name: user!.name || '',
                  email: user!.email,
                  isAdmin: user!.isAdmin,
                  tier: manualSubTier as 'FREE' | 'BASIC' | 'PRO' | 'AGENCY',
                  subscriptionStatus: 'active',
                  subscriptionEndsAt: moment(manualSubEndsAt).endOf('day').toISOString(),
                  usedFreeMonthOffer: user!.usedFreeMonthOffer
                });
              }}
              disabled={updateUserMutation.isPending}
            >
              Confirm Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
